import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getPropProgressSeenValue,
  markPropProgressSeenValue,
  resolvePropProgressCelebration,
  type PropCelebrationKind,
} from '@/utils/propProgressCelebration';

const FILL_DELAY_MS = 40;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function toNumericValue(value: number | null | undefined): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

type UsePropProgressCelebrationArgs = {
  enabled: boolean;
  storageKey: string | null | undefined;
  statType: string | null | undefined;
  currentValue: number | null | undefined;
};

/**
 * Overlay a scoring Lottie when prop progress increases, then animate the bar.
 *
 * Live: polls raise `currentValue` while the user is on the page.
 * Return: unseen progress in localStorage plays once, then the bar fills to the new value.
 */
export function usePropProgressCelebration({
  enabled,
  storageKey,
  statType,
  currentValue,
}: UsePropProgressCelebrationArgs) {
  const numericCurrent = toNumericValue(currentValue);
  const currentRef = useRef(numericCurrent);
  currentRef.current = numericCurrent;

  const lastSeenRef = useRef<number | null>(
    storageKey ? getPropProgressSeenValue(storageKey) : null
  );

  const [isPlaying, setIsPlaying] = useState(false);
  const [kind, setKind] = useState<PropCelebrationKind | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [displayedValue, setDisplayedValue] = useState(() => {
    const resolved = resolvePropProgressCelebration({
      enabled,
      statType,
      currentValue: numericCurrent,
      lastSeenValue: lastSeenRef.current,
      reducedMotion: prefersReducedMotion(),
    });
    return resolved.displayedValue;
  });
  const [emphasizeFill, setEmphasizeFill] = useState(false);

  const playingRef = useRef(false);
  playingRef.current = isPlaying;

  useEffect(() => {
    if (storageKey) {
      const stored = getPropProgressSeenValue(storageKey);
      if (stored != null && lastSeenRef.current == null) {
        lastSeenRef.current = stored;
      }
    }
  }, [storageKey]);

  useEffect(() => {
    if (playingRef.current) return;

    const resolved = resolvePropProgressCelebration({
      enabled,
      statType,
      currentValue: numericCurrent,
      lastSeenValue: lastSeenRef.current,
      reducedMotion: prefersReducedMotion(),
    });

    setKind(resolved.kind);
    setSrc(resolved.src);

    if (!enabled) {
      setDisplayedValue(numericCurrent);
      setIsPlaying(false);
      setEmphasizeFill(false);
      return;
    }

    if (!resolved.kind) {
      setDisplayedValue(numericCurrent);
      if (storageKey && lastSeenRef.current == null) {
        markPropProgressSeenValue(storageKey, numericCurrent);
        lastSeenRef.current = numericCurrent;
      }
      return;
    }

    if (resolved.shouldPlay) {
      setDisplayedValue(resolved.displayedValue);
      setEmphasizeFill(false);
      setIsPlaying(true);
      return;
    }

    if (lastSeenRef.current == null && numericCurrent <= 0 && storageKey) {
      markPropProgressSeenValue(storageKey, 0);
      lastSeenRef.current = 0;
    }

    setDisplayedValue(resolved.displayedValue);
    setIsPlaying(false);
  }, [enabled, statType, numericCurrent, storageKey]);

  const complete = useCallback(() => {
    const target = currentRef.current;
    if (storageKey) {
      markPropProgressSeenValue(storageKey, target);
    }
    lastSeenRef.current = target;
    setIsPlaying(false);
    setEmphasizeFill(true);
    window.setTimeout(() => {
      setDisplayedValue(target);
    }, FILL_DELAY_MS);
  }, [storageKey]);

  return {
    isPlaying,
    kind,
    src,
    displayedValue,
    emphasizeFill,
    complete,
  };
}
